import {
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import {
    getAvailableAccountTypes,
    isAccountSubtypeValidForType,
    isAccountTypeValidForClass,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import { ImportedAccount } from "@common/schemas/domains/accounting/importedAccount.schema";
import { Account } from "@common/types/domains/accounting/account";

export interface CreateAccountFromRowInitialValues {
    name: string;
    type: AccountType;
    subtype: AccountSubtype;
    parentGroupId: string | null;
}

export function resolveCreateAccountFromRowInitialValues({
    account,
    mappedKickAccount,
    typedName,
}: {
    account: Pick<ImportedAccount, "name" | "classification" | "kickType">;
    mappedKickAccount:
        | Pick<Account, "type" | "subtype" | "parentGroupId">
        | undefined;
    typedName: string | undefined;
}): CreateAccountFromRowInitialValues {
    const name = typedName ?? account.name;
    if (
        mappedKickAccount &&
        isAccountTypeValidForClass(
            mappedKickAccount.type,
            account.classification,
        )
    ) {
        return {
            name,
            type: mappedKickAccount.type,
            subtype: mappedKickAccount.subtype,
            parentGroupId: mappedKickAccount.parentGroupId,
        };
    }
    const type =
        account.kickType !== null &&
        isAccountTypeValidForClass(account.kickType, account.classification)
            ? account.kickType
            : getAvailableAccountTypes([account.classification])[0];
    // The form doesn't submit the subtype; it only needs one valid for the type.
    const subtype =
        Object.values(AccountSubtype).find((candidate) =>
            isAccountSubtypeValidForType(candidate, type),
        ) ?? AccountSubtype.cashOnHand;
    return { name, type, subtype, parentGroupId: null };
}

export function mapCreatedAccountToRow({
    createdAccounts,
    entityId,
    importedAccountId,
    onMapAccount,
}: {
    createdAccounts: Array<Pick<Account, "id"> & { entityId?: number }>;
    entityId: number;
    importedAccountId: string;
    onMapAccount: (params: {
        importedAccountId: string;
        accountId: string | null;
    }) => void;
}): void {
    const createdAccount = createdAccounts.find(
        (candidate) => candidate.entityId === entityId,
    );
    if (createdAccount) {
        onMapAccount({ importedAccountId, accountId: createdAccount.id });
    }
}
